/**
 * SettingsMenu.tsx
 *
 * Comprehensive settings menu with tabbed interface
 * Based on Ego Somnium settings (excluding image generation)
 */
import React, { useState } from 'react';
import { useTheme } from '../../services/ThemeContext';
import { ScrollableModal } from './Modal';
import { EulaModal } from './EulaModal';
import { PrivacyPolicyModal } from './PrivacyPolicyModal';
import { NarrativeSettings } from './NarrativeSettings';
import { ColorSchemeModal } from './ColorSchemeModal';
import { FontSettingsModal } from './FontSettingsModal';
import { TransparencySettingsModal } from './TransparencySettingsModal';
import { AchievementsSettings } from './AchievementsSettings';
import { HelpSettings } from './HelpSettings';
import FeedbackModal from './FeedbackModal';

interface SettingsMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onDonate?: () => void;
}

type TabType = 'interface' | 'info' | 'legal';
type ViewType = 'main' | 'narrative' | 'achievements' | 'help';

const SettingsMenu: React.FC<SettingsMenuProps> = ({ isOpen, onClose, onDonate }) => {
  const { currentTheme: colors } = useTheme();
  const [activeTab, setActiveTab] = useState<TabType>('interface');
  const [view, setView] = useState<ViewType>('main');
  const [showEula, setShowEula] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showColorScheme, setShowColorScheme] = useState(false);
  const [showFontSettings, setShowFontSettings] = useState(false);
  const [showTransparencySettings, setShowTransparencySettings] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  // Helper to create nav button style
  const navButtonStyle = {
    width: '100%',
    display: 'flex' as const,
    justifyContent: 'space-between' as const,
    alignItems: 'center' as const,
    padding: '16px 12px',
    backgroundColor: 'transparent',
    border: 'none',
    color: colors.textMain,
    fontSize: 16,
    cursor: 'pointer',
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    borderBottomStyle: 'solid' as const,
  };

  // Tab bar component
  const TabBar = () => (
    <div style={{ display: 'flex', borderBottomWidth: 1, borderBottomColor: colors.border, borderBottomStyle: 'solid', marginBottom: 8 }}>
      <button
        data-testid="settings-tab-interface"
        onClick={() => setActiveTab('interface')}
        style={{
          flex: 1,
          padding: '12px 8px',
          background: 'none',
          border: 'none',
          borderBottomWidth: activeTab === 'interface' ? 2 : 1,
          borderBottomColor: activeTab === 'interface' ? colors.primary : colors.border,
          borderBottomStyle: 'solid',
          color: activeTab === 'interface' ? colors.primary : colors.textMuted,
          fontSize: 14,
          fontWeight: '600',
          cursor: 'pointer',
        }}
      >
        Interface
      </button>
      <button
        data-testid="settings-tab-info"
        onClick={() => setActiveTab('info')}
        style={{
          flex: 1,
          padding: '12px 8px',
          background: 'none',
          border: 'none',
          borderBottomWidth: activeTab === 'info' ? 2 : 1,
          borderBottomColor: activeTab === 'info' ? colors.primary : colors.border,
          borderBottomStyle: 'solid',
          color: activeTab === 'info' ? colors.primary : colors.textMuted,
          fontSize: 14,
          fontWeight: '600',
          cursor: 'pointer',
        }}
      >
        Info
      </button>
      <button
        data-testid="settings-tab-legal"
        onClick={() => setActiveTab('legal')}
        style={{
          flex: 1,
          padding: '12px 8px',
          background: 'none',
          border: 'none',
          borderBottomWidth: activeTab === 'legal' ? 2 : 1,
          borderBottomColor: activeTab === 'legal' ? colors.primary : colors.border,
          borderBottomStyle: 'solid',
          color: activeTab === 'legal' ? colors.primary : colors.textMuted,
          fontSize: 14,
          fontWeight: '600',
          cursor: 'pointer',
        }}
      >
        Legal
      </button>
    </div>
  );

  // Support the Developer button component
  const SupportDeveloperButton = () => {
    if (!onDonate) return null;
    return (
      <button
        onClick={onDonate}
        style={{
          width: '100%',
          padding: '8px',
          borderRadius: 8,
          display: 'flex',
          flexDirection: 'row' as const,
          alignItems: 'center',
          backgroundColor: `${colors.primary}15`,
          borderWidth: 1,
          borderColor: colors.primary,
          borderStyle: 'solid' as const,
          cursor: 'pointer',
        }}
      >
        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', paddingRight: 15 }}>
          <span style={{ fontSize: 20 }}>☕</span>
        </div>
        <div style={{ flex: 1, textAlign: 'center', whiteSpace: 'nowrap' }}>
          <div style={{ fontSize: 14, fontWeight: 'bold', color: colors.primary }}>
            Support the Developer & help keep this app free
          </div>
        </div>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'flexStart', paddingLeft:15 }}>
          <span style={{ fontSize: 20 }}>💜</span>
        </div>
      </button>
    );
  };

  // Tab content components
  const InterfaceTab = () => (
    <div>
      <button data-testid="settings-nav-color-scheme" onClick={() => setShowColorScheme(true)} style={navButtonStyle}>
        <span>Color Scheme</span>
        <span style={{ color: colors.primary }}>→</span>
      </button>
      <button data-testid="settings-nav-font" onClick={() => setShowFontSettings(true)} style={navButtonStyle}>
        <span>Font Settings</span>
        <span style={{ color: colors.primary }}>→</span>
      </button>
      <button data-testid="settings-nav-transparency" onClick={() => setShowTransparencySettings(true)} style={navButtonStyle}>
        <span>Text Area Transparency</span>
        <span style={{ color: colors.primary }}>→</span>
      </button>
      <button data-testid="settings-nav-narrative" onClick={() => setView('narrative')} style={navButtonStyle}>
        <span>Narrative Settings</span>
        <span style={{ color: colors.primary }}>→</span>
      </button>
    </div>
  );

  const InfoTab = () => (
    <div>
      <button data-testid="settings-nav-achievements" onClick={() => setView('achievements')} style={navButtonStyle}>
        <span>Achievements</span>
        <span style={{ color: colors.primary }}>→</span>
      </button>
      <button data-testid="settings-nav-help" onClick={() => setView('help')} style={navButtonStyle}>
        <span>Help</span>
        <span style={{ color: colors.primary }}>→</span>
      </button>
      <button data-testid="settings-nav-feedback" onClick={() => setShowFeedback(true)} style={navButtonStyle}>
        <span>Send Feedback / Report Bug</span>
        <span style={{ color: colors.primary }}>→</span>
      </button>
    </div>
  );

  const LegalTab = () => (
    <div>
      <button data-testid="settings-nav-eula" onClick={() => setShowEula(true)} style={navButtonStyle}>
        <span>End User License Agreement (EULA)</span>
        <span style={{ color: colors.primary }}>→</span>
      </button>
      <button data-testid="settings-nav-privacy" onClick={() => setShowPrivacy(true)} style={navButtonStyle}>
        <span>Privacy Policy</span>
        <span style={{ color: colors.primary }}>→</span>
      </button>
    </div>
  );

  const MainSettings = () => (
    <>
      {/* Tab Bar */}
      <TabBar />

      {/* Tab Content */}
      <div>
        {activeTab === 'interface' && <InterfaceTab />}
        {activeTab === 'info' && <InfoTab />}
        {activeTab === 'legal' && <LegalTab />}
      </div>
    </>
  );

  // Render different views
  if (showEula) {
    return <EulaModal isOpen={showEula} onClose={() => setShowEula(false)} />;
  }
  if (showPrivacy) {
    return <PrivacyPolicyModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} />;
  }

  // Render sub-page views
  if (view === 'narrative') {
    return <NarrativeSettings isOpen={view === 'narrative'} onClose={() => setView('main')} />;
  }
  if (view === 'achievements') {
    return <AchievementsSettings isOpen={view === 'achievements'} onClose={() => setView('main')} />;
  }
  if (view === 'help') {
    return <HelpSettings isOpen={view === 'help'} onClose={() => setView('main')} />;
  }

  // Main settings menu
  return (
    <>
      <ScrollableModal isOpen={isOpen} onClose={onClose} title="App Settings" height="500px" footer={<SupportDeveloperButton />}>
        <MainSettings />
      </ScrollableModal>

      {/* Color Scheme Modal */}
      <ColorSchemeModal isOpen={showColorScheme} onClose={() => setShowColorScheme(false)} />

      {/* Font Settings Modal */}
      <FontSettingsModal isOpen={showFontSettings} onClose={() => setShowFontSettings(false)} />

      {/* Transparency Settings Modal */}
      <TransparencySettingsModal isOpen={showTransparencySettings} onClose={() => setShowTransparencySettings(false)} />

      {/* Feedback Modal */}
      <FeedbackModal isOpen={showFeedback} onClose={() => setShowFeedback(false)} />
    </>
  );
};

export default SettingsMenu;
